import {isBefore} from "date-fns";
import {
    AgentPresence,
    ScheduledTransfer,
    Transfer,
    Tripleg,
    Truck,
    TruckStamp,
    useLastTriplegForTruck,
    useLastValidTruckById,
    VenueStub,
    watchStampsForTruckTqo
} from "../rpc/webrpc-client.gen.ts";
import {useActiveCrewPresencesForTruck, useAwaitingScheduledActions, useMothership,} from "../tq/tqx.tsx";
import {useMemo} from "react";
import {z} from "zod/v4";
import {useBundledFullOpenTransfersForTruck} from "../tq/tqxx.tsx";
import {InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {cleanse} from "../util/arutil.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";

export const TruckStatus = z.object({
    truck: Truck,
    lastTripleg: z.optional(Tripleg),
    currentPlace: z.optional(VenueStub),
    currentDestination: z.optional(VenueStub),
    canDepart: z.boolean(),
    currentCrew: AgentPresence.array(),
    canPickup: ScheduledTransfer.array(),
    canDropoff: z.union([Transfer, ScheduledTransfer]).array(),
    remainingDropoffs: Transfer.array(),
    isEmpty: z.boolean(),
});
export type TruckStatus = z.infer<typeof TruckStatus>;

// couple of problems here, one hopefully minor one is that there can only
// be at most one active scheduled dropoff for a given truck. otherwise,
// we can't give a definitive destination in the truck status, which sounds
// like an important thing to give up.

// a much larger issue is that there isn't a clean way to query the api for
// dropoffs given a truck id. but you shouldn't be relying on truck status
// for that, so even though canDropoff could theoretically give you scheduled
// things, don't rely on it to. if you are needing to know this as an admitter,
// you know where you are. if you are needing to know it as a dispatcher, hmm...
// this might need to be rethought.

// 2024.09.23: yeah, it did need to be rethought, because it is directly in the
// dispatch truck tile's wheelhouse. in fact, the ordinary truck tile is versatile
// to cover dispatch, but for the fact that it needs a last tripleg scene.
// it's intended to be plugged into the middle of a tile stack.
// admitters won't care because, as i said before, you know where you are and don't
// want to see it over and over on your screen.

export function useTruckStatus(args?: { truckId: string }): InfallibleUseResult<TruckStatus> {
    const truckTqrv = useLastValidTruckById(args);
    const triplegTqrv = useLastTriplegForTruck(args);
    const crewPresencesTqrv = useActiveCrewPresencesForTruck(args);
    // useEffect(() => {
    //     return () => {
    //         ignoreActiveCrewPresencesForTruck({truckId});
    //     }
    // }, [truckId]);

    const enableAwaitingScheduledTransferArgs
        = (args && triplegTqrv.data)
        ? {truckId: args.truckId, venueId: triplegTqrv.data.destination.id}
        : undefined;
    const awaitingScheduledTransfersTqrv
        = useAwaitingScheduledActions(enableAwaitingScheduledTransferArgs);

    const openTransfersTqrv
        = useBundledFullOpenTransfersForTruck(args);

    // useWhatChanged([awaitingScheduledTransfers, crewPresences, destinationId, enableAwaitingScheduledTransfers, isAwaitingScheduledTransfersSuccess, isCrewSuccess, isOpenTransfersSuccess, isOpenXTransfersSuccess, isTriplegSuccess, isTruckSuccess, openTransferStubs.length, openXTransfers, tripleg, truckData?.truck])
    const rv = useMemo(() => {
        if (truckTqrv.isPending || triplegTqrv.isPending || crewPresencesTqrv.isPending
            || (!!triplegTqrv.data && awaitingScheduledTransfersTqrv.isPending)
            || openTransfersTqrv.isPending) {
            return pendingUseResult;
        }
        const cleanCrewPresences = cleanse(crewPresencesTqrv.data);

        const rv: TruckStatus = {
            truck: truckTqrv.data,
            lastTripleg: triplegTqrv.data,
            currentCrew: cleanCrewPresences,
            currentPlace: undefined,
            currentDestination: undefined,
            canDepart: false,
            canPickup: [],
            canDropoff: [],
            remainingDropoffs: [],
            isEmpty: openTransfersTqrv.data.length === 0,
        };

        const tripleg = triplegTqrv.data;
        if (awaitingScheduledTransfersTqrv.data) {
            for (const sxfer of awaitingScheduledTransfersTqrv.data) {
                if (!sxfer) {
                    continue;
                }
                // ignore anything posted after we arrived, so that we don't
                // get stuck in an endless loop unable to leave until the thing
                // we promised to come back for later is loaded
                // the null assertions work because if we are someplace that
                // has something for us to do, we must have gotten here somehow.
                // that can't happen on a brand-new truck, which is the only
                // way it has no last tripleg. however, it is possible that the
                // arrival has not fully made it back from the server yet, so
                // we must be prepared for arrived being empty. the good news
                // is that in that case, we know we have only just gotten here
                // and any new pickups coming in are legit and we want them to
                // block departure.
                if (!tripleg?.arrived || isBefore(sxfer.posted, tripleg.arrived)) {
                    switch (sxfer.direction) {
                        case "PICKUP":
                            rv.canPickup.push(sxfer);
                            break;
                        case "DROPOFF":
                            rv.canDropoff.push(sxfer);
                            break;
                    }
                }
            }
        }
        const openXTransfers = cleanse(openTransfersTqrv.data);
        const destinationId = tripleg?.destination.id;
        if (destinationId) {
            for (const transfer of openXTransfers) {
                const bundle = transfer.bundle;
                if (transfer.offloadVenue?.id === destinationId
                    || (bundle.destination?.id === destinationId)) {
                    rv.canDropoff.push(transfer);
                } else {
                    rv.remainingDropoffs.push(transfer);
                }
            }
        }
        if (!tripleg) {
            rv.canDepart = true;
        } else {
            let blockingTransfer = false;
            rv.currentDestination = tripleg.destination;
            if (tripleg.admitted) {
                rv.currentPlace = tripleg.destination;
                delete rv.currentDestination;
                if (openXTransfers.length == 0) {
                    /* empty */
                } else {
                    const activeOffload = openXTransfers.find(maybe =>
                        !!maybe.offloadStarted && !maybe.offloadEnded);
                    const activeOnload = openXTransfers.find(maybe =>
                        !!maybe.onloadStarted && !maybe.onloadEnded);
                    if (activeOffload ?? activeOnload) {
                        blockingTransfer = true;
                    }
                }
            }

            rv.canDepart = rv.canPickup.length === 0 && rv.canDropoff.length === 0
                && !blockingTransfer;
        }

        return successUseResult(rv);
    }, [awaitingScheduledTransfersTqrv.data, awaitingScheduledTransfersTqrv.isPending,
        crewPresencesTqrv.data, crewPresencesTqrv.isPending,
        openTransfersTqrv.data, openTransfersTqrv.isPending,
        triplegTqrv.data, triplegTqrv.isPending, truckTqrv.data, truckTqrv.isPending]);

    return rv;
}

export function useLastFlavoredTruckStamp(args?: { truckId: string }): InfallibleUseResult<TruckStamp | null> {
    const mama = useMothership();
    return useRefreshableInfallibleQuery({
        ...watchStampsForTruckTqo(mama, args),
        select: wrapped =>
            wrapped.stamps.findLast(maybe => !!maybe.flavor) ?? null,
    });
}

// export const lastTruckSceneVerb = "WatchLastTruckScene";
//
// export function lastTruckSceneTqk(args: { truckId: string }) {
//     return [lastTruckSceneVerb, args];
// }
//
// function figureLastTruckScene(food: FigureLastTruckSceneFood): TruckScene | null {
//     let rv: TruckScene | null = null;
//
//     if (food.lastTripleg) {
//         rv = {
//             ...getLastTriplegScene(food.lastTripleg),
//             asof: food.lastTriplegUpdatedAt ?? food.lastTripleg.lastModified
//         };
//     }
//
//     if (food.lastFlavoredStamp) {
//         const stamp = food.lastFlavoredStamp;
//         if (!rv || isBefore(rv.when, stamp.posted)) {
//             rv = {
//                 subjectFlavor: "truckStamp",
//                 when: stamp.posted,
//                 asof: food.stampsUpdatedAt ?? stamp.posted,
//                 stampFlavor: stamp.flavor!,
//             };
//         }
//     }
//
//     if (food.crewTenures) {
//         let lastHap: DateArg<Date> | undefined;
//         if (!rv || isBefore(rv.when, food.crewUpdatedAt)) {
//             rv = {
//                 subjectFlavor: "truckCrew",
//                 when: food.crewUpdatedAt,
//                 asof: food.crewUpdatedAt,
//                 activeCrew: food.activeCrew,
//             };
//         }
//     }
//
//     for (const xfer of food.openTransfers) {
//
//     }
//
//     return rv;
// }
